/**
 * [T-WIDGET-REDESIGN-V1] Design tokens for the FinTrack dashboard widget system.
 *
 * Single source of truth for every visual constant used by widget skins, bodies,
 * and primitives. Replaces the legacy 6-color chart cycle (--chart-1..5) with a
 * 5-token financial semantic palette that carries meaning (positive/negative/
 * neutral/secondary/warning) rather than arbitrary hues.
 *
 * ## Reading order
 *   1. DATA_TOKENS         — semantic financial colors (light + dark)
 *   2. CATEGORICAL_PALETTE — 6-color set for unrelated series (no red/green)
 *   3. TYPE_SCALE          — Tailwind class strings for typography
 *   4. CHART_MARGIN        — shared chart margin preset
 *   5. CHART_HEIGHT        — height variants
 *   6. ANIMATION           — CSS animation durations (ms)
 *   7. A11Y                — WCAG 2.2 AA targets and touch standards
 *
 * ## Usage
 *   import { DATA_TOKENS, useDataToken } from '@/lib/design/widgetTokens';
 *   const positive = useDataToken('positive'); // "var(--data-positive)" or hsl fallback
 *
 * @see .omo/plans/dashboard-widgets-redesign.md §3.2
 */

export type DataTokenName = keyof typeof DATA_TOKENS;
export type CategoricalIndex = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Semantic financial data tokens.
 * Each token has a `light` + `dark` HSL string + a human-readable `name`.
 * Use `useDataToken(name)` to resolve at render time.
 */
export const DATA_TOKENS = {
  /** Income, gain, on-track, healthy, positive delta */
  positive: {
    light: 'hsl(150 45% 36%)',
    dark: 'hsl(150 40% 50%)',
    name: 'Income / Gain / On-track',
  },
  /** Expense, loss, over-budget, negative delta */
  negative: {
    light: 'hsl(8 60% 45%)',
    dark: 'hsl(8 55% 58%)',
    name: 'Expense / Loss / Over-budget',
  },
  /** Primary, total, focus, neutral metric */
  neutral: {
    light: 'hsl(220 70% 52%)',
    dark: 'hsl(220 65% 64%)',
    name: 'Primary / Total / Focus',
  },
  /** Secondary series, comparison */
  secondary: {
    light: 'hsl(180 35% 36%)',
    dark: 'hsl(180 30% 50%)',
    name: 'Secondary series',
  },
  /** Approaching limit, attention, near-threshold */
  warning: {
    light: 'hsl(35 75% 48%)',
    dark: 'hsl(35 70% 60%)',
    name: 'Approaching limit',
  },
} as const;

/**
 * 6-color categorical palette for charts with ≥3 unrelated series.
 * Wong-inspired, OKLCH-balanced, colorblind-safe, no red/green pairing.
 * Use sequential indices (`categorical.1`, `categorical.2`, ...).
 */
export const CATEGORICAL_PALETTE: readonly string[] = [
  'hsl(220 70% 52%)',
  'hsl(180 35% 40%)',
  'hsl(35 75% 50%)',
  'hsl(280 40% 55%)',
  'hsl(200 25% 50%)',
  'hsl(15 50% 50%)',
] as const;

/**
 * Typography scale.
 * `tabular-nums` is used on every numeric value to align decimal points in tables.
 */
export const TYPE_SCALE = {
  kpiHero: 'text-3xl font-semibold tabular-nums tracking-tight',
  kpiLabel: 'text-sm text-muted-foreground',
  chartLabel: 'text-xs text-muted-foreground',
  axisLabel: 'text-[10px] text-muted-foreground tabular-nums',
  tooltip: 'text-xs font-medium',
  caption: 'text-[10px] text-muted-foreground/70',
} as const;

/** Shared chart margin (px) used by all Recharts wrappers. */
export const CHART_MARGIN = { top: 8, right: 12, left: 0, bottom: 0 } as const;

/** Chart height variants (px). */
export const CHART_HEIGHT = { sm: 80, md: 160, lg: 240, xl: 320 } as const;

/** CSS animation durations (ms). Respect prefers-reduced-motion. */
export const ANIMATION = {
  dataTransition: 500,
  hoverFade: 150,
  progressFill: 600,
  mountStagger: 50,
} as const;

/** WCAG 2.2 AA accessibility targets. */
export const A11Y = {
  /** :1 ratio minimum for body text */
  textContrast: 4.5,
  /** :1 ratio minimum for large text (≥18pt or ≥14pt bold) */
  largeContrast: 3,
  /** :1 ratio minimum for non-text UI elements */
  uiContrast: 3,
  /** px width of focus ring outline */
  focusRingWidth: 2,
  /** px minimum touch target size (Apple HIG / WCAG 2.5.5) */
  touchTargetSize: 44,
} as const;

/**
 * Resolve a data token to its CSS color string for the current theme.
 * Returns `var(--data-{name})` (resolved by CSS at paint time) with a
 * `hsl(...)` fallback for SSR / non-CSS contexts.
 */
export function useDataToken(name: DataTokenName): string {
  if (typeof document === 'undefined') {
    return DATA_TOKENS[name].light;
  }
  return `var(--data-${name})`;
}

/**
 * Resolve a categorical palette index to its HSL string.
 * Indices ≥6 wrap around (mod 6).
 */
export function useCategoricalColor(index: CategoricalIndex): string {
  return CATEGORICAL_PALETTE[((index - 1) % 6 + 6) % 6];
}

// ─────────────────────────────────────────────────────────────────────────────
// Backward Compatibility
// ─────────────────────────────────────────────────────────────────────────────
//
// v1 → v2 token rename: `*-finance-*` → `*-data-*`
// ─────────────────────────────────────────────────────────────────────────────
// In the v1 widget system, Tailwind utilities were scoped under the `finance-`
// prefix (e.g. `text-finance-positive`, `bg-finance-negative/10`). In v2 those
// tokens were renamed to the more semantically generic `data-` prefix
// (e.g. `text-data-positive`, `bg-data-negative/10`) to free the `finance-`
// namespace for future use and to align with the corresponding CSS custom
// properties (`--data-positive`, `--data-negative`, …) defined in
// `frontend/src/index.css` and the Tailwind theme extension in
// `frontend/tailwind.config.ts`.
//
// `migrateFinanceTokenName()` rewrites any legacy `*-finance-*` class names
// found in a className string to their v2 `*-data-*` equivalents. It is
// intended for the `FeatureFlagBoundary` "fallback" path (Task 19) so that
// legacy widget skins continue to render correctly during the v1 → v2
// transition without forcing callers to update every className by hand.
//
// **Deprecation policy**: This helper is a temporary migration aid. It will
// be removed once all legacy v1 widget skins have been ported to v2 tokens
// (target: end of the dashboard-widgets-redesign rollout). New code MUST
// reference `*-data-*` utilities directly — do not write new `*-finance-*`
// classes anywhere in the codebase.

/**
 * Backward-compat migration for old `*-finance-*` Tailwind classes.
 * Maps them to the new `*-data-*` semantic tokens.
 *
 * Used by `FeatureFlagBoundary`'s "fallback" path (Task 19) to keep legacy
 * widget skins rendering correctly while users transition to v2.
 *
 * Handles the three Tailwind utility prefixes (`text-`, `bg-`, `border-`)
 * across all five semantic tokens (`positive`, `negative`, `neutral`,
 * `secondary`, `warning`) — 15 distinct replacements in total. Operates on
 * raw className strings (space-separated tokens) using `String#replace`
 * with the global flag for broad runtime compatibility (avoids the ES2021
 * `String#replaceAll`, which is unavailable in older targets).
 *
 * @example
 *   migrateFinanceTokenName('text-finance-positive bg-finance-negative/10')
 *   // => 'text-data-positive bg-data-negative/10'
 *
 * @param className - The className string potentially containing legacy `*-finance-*` tokens.
 * @returns A new string with all `*-finance-*` tokens rewritten to `*-data-*`.
 */
export function migrateFinanceTokenName(className: string): string {
  return className
    .replace(/text-finance-/g, 'text-data-')
    .replace(/bg-finance-/g, 'bg-data-')
    .replace(/border-finance-/g, 'border-data-');
}
